/**
 * Runtime Config Feature Flag Provider
 * Loads feature flags from window.SCANTIX_CONFIG
 * Useful for server-side configuration without rebuilding
 */

import type {
  FeatureFlagProvider,
  FeatureFlags,
} from "../feature-flags.interface";
import { createLogger } from "@/services/logger/logger.service";

const logger = createLogger("RuntimeConfigProvider");
import { DEFAULT_FEATURE_FLAGS } from "../feature-flags.config";

export class RuntimeConfigFeatureFlagProvider implements FeatureFlagProvider {
  private flags: FeatureFlags;

  constructor() {
    this.flags = this.loadFlags();
  }

  get<K extends keyof FeatureFlags>(key: K): FeatureFlags[K] {
    return this.flags[key];
  }

  set<K extends keyof FeatureFlags>(key: K, _value: FeatureFlags[K]): void {
    // Runtime config is read-only
    logger.warn(`Cannot set feature flag ${key} in runtime config provider`);
  }

  getAll(): FeatureFlags {
    return { ...this.flags };
  }

  reset(): void {
    // Runtime config is read-only
    logger.warn("Cannot reset runtime config feature flags");
  }

  isEnabled(key: keyof FeatureFlags): boolean {
    const value = this.get(key);
    return typeof value === "boolean" ? value : false;
  }

  private loadFlags(): FeatureFlags {
    const windowConfig =
      (
        window as unknown as {
          SCANTIX_CONFIG?: { FEATURE_FLAGS?: Partial<FeatureFlags> };
          APP_CONFIG?: { FEATURE_FLAGS?: Partial<FeatureFlags> };
        }
      ).SCANTIX_CONFIG ||
      (
        window as unknown as {
          SCANTIX_CONFIG?: { FEATURE_FLAGS?: Partial<FeatureFlags> };
          APP_CONFIG?: { FEATURE_FLAGS?: Partial<FeatureFlags> };
        }
      ).APP_CONFIG;

    if (windowConfig?.FEATURE_FLAGS) {
      // Merge with defaults to handle missing flags
      return { ...DEFAULT_FEATURE_FLAGS, ...windowConfig.FEATURE_FLAGS };
    }

    return { ...DEFAULT_FEATURE_FLAGS };
  }
}
